import type { BffApiClient } from "@/app/api-client"
import {
  CollectionSearchSort,
  CollectionSearchSortDirection,
} from "@anaconda/core/nft-collection/search/types"
import type { InferRequestType } from "hono"
import { z } from "zod"
import {
  AVAILABLE_STATISTIC_PERIODS,
  DEFAULT_SORT,
  INITIAL_FILTERS,
  getDefaultStatisticPeriod,
  resolveStatisticPeriod,
} from "./default-values"
import type { ScopeState } from "./store.reatom"

export type QueryInput = InferRequestType<
  BffApiClient["api"]["collections"]["leaderboard"]["$post"]
>["json"]

export const getCollectionLeaderboardQueryParams = (
  apiClient: BffApiClient,
  queryInput: QueryInput,
) => {
  const input = {
    ...queryInput,
    blockchains: queryInput.blockchains,
  }

  return {
    queryKey: ["collections", "leaderboard", input],
    queryFn: async () => {
      const response = await apiClient.api.collections.leaderboard.$post(
        {
          json: input,
        },
        {
          init: {
            next: {
              // Cache collection leaderboard response on next server for 5 minutes
              revalidate: 10,
            },
          },
        },
      )

      if (!response.ok) throw await response.json()

      return response.json()
    },
  }
}

type QueryFn = ReturnType<typeof getCollectionLeaderboardQueryParams>["queryFn"]

export type QueryResponse = Awaited<ReturnType<QueryFn>>

export const SEARCH_PARAMS_SCHEMA = z.object({
  period: z.enum(AVAILABLE_STATISTIC_PERIODS).optional(),
  sortBy: z.nativeEnum(CollectionSearchSort).optional(),
  sortDirection: z.nativeEnum(CollectionSearchSortDirection).optional(),
})

/** `blockchain` as the page names it: a `Blockchain`, "ALL", or a URL slug. */
export function getUrlSearchParams(
  url: URL,
  filters: Pick<ScopeState["filters"], "statisticPeriod">,
  sort: ScopeState["sort"],
  blockchain: string,
) {
  const search = new URLSearchParams(url.search)

  if (filters.statisticPeriod !== getDefaultStatisticPeriod(blockchain)) {
    search.set("period", filters.statisticPeriod)
  } else {
    search.delete("period")
  }

  if (
    sort.field === DEFAULT_SORT.field &&
    sort.direction === DEFAULT_SORT.direction
  ) {
    search.delete("sortBy")
    search.delete("sortDirection")
  } else {
    search.set("sortBy", sort.field)
    search.set("sortDirection", sort.direction)
  }

  return search
}

/**
 * The leaderboard state a URL asks for on `blockchain`: its period if the chain
 * offers it (see getAvailableStatisticPeriods), else the chain's default
 * period, which it also gets without one.
 */
export function getStateFromSearchParams(
  searchParams: z.infer<typeof SEARCH_PARAMS_SCHEMA>,
  blockchain: string,
): ScopeState {
  const period = searchParams.period
  const sortBy = searchParams.sortBy
  const sortDirection = searchParams.sortDirection

  const filters = {
    ...INITIAL_FILTERS,
    statisticPeriod: resolveStatisticPeriod(
      blockchain,
      period ?? getDefaultStatisticPeriod(blockchain),
    ),
  }

  const sort = {
    field: sortBy ?? DEFAULT_SORT.field,
    direction: sortDirection ?? DEFAULT_SORT.direction,
  }

  return { filters, sort }
}
